import { checkoutAccommodationGroupingKey } from 'checkout/selectors/view/accommodation'
import { createSelector } from 'reselect'
import { groupBy, nonNullable, max } from 'lib/array/arrayUtils'
import { countOccupants, countTravellersAsOccupants, getOccupanciesFromReservations, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getInsuranceEnabledCheckoutItems } from '../view/insurance'
import { isStandaloneFlights } from '../view/flights'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'
import getInsuranceFlightOccupants from './getInsuranceFlightOccupants'
import getExperienceParticipantOccupants from './getExperienceParticipantOccupants'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { isAccommodationItem } from 'lib/checkout/checkoutUtils'
import { mergeOccupanciesByMaxPerType, normaliseOccupancyForInsurance, occupancyGroupsAsMergeSources } from 'lib/insurance/insuranceOccupants'
import { getBookingProtectionOccupantsFromCartItems, getExperienceTicketCountFallbackOccupants } from './bookingProtectionOccupants'

/**
 * Leaf selectors for insurance quote occupancy / travellers.
 * Kept out of request/insurance.ts so getCheckoutInsuranceQuoteParams can import them
 * without re-entering insurance.ts mid-init through the bookingProtection ↔ totals cycle.
 */

const getTotalOccupancyCount = (occupancy: App.Occupants): number => {
  return sumUpOccupancies([occupancy])
}

const getInsuranceOccupantsByAccommodationGroup = createSelector(
  (state: App.State) => getInsuranceEnabledCheckoutItems(state),
  (items) => {
    const accommodationItems = items.filter(item => isAccommodationItem(item))
    if (accommodationItems.length > 0) {
      const itemMap = groupBy(accommodationItems, checkoutAccommodationGroupingKey)

      return Array.from(itemMap.values()).map((itemList) => {
        const occupants = nonNullable(itemList.map(item => item.occupancy))
        return countOccupants(occupants)
      })
    }
  },
)

export const getOccupantsFromCartItems = createSelector(
  (state: App.State) => getInsuranceOccupantsByAccommodationGroup(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => getInsuranceFlightOccupants(state),
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => getExperienceTicketCountFallbackOccupants(state),
  (state: App.State) => getExperienceParticipantOccupants(state),
  (accommodationOccupants, isStandaloneFlight, flightOccupancies, isStandaloneExperience, experienceFallbackOccupants, experienceParticipantOccupants) => {
    if (isStandaloneExperience) {
      /**
       * Standalone experiences: quote occupancy must count the same expanded
       * participants (participantsPerUnit, adult/child fares) as the traveller
       * forms the order submits, or the provider rejects the booking after
       * payment. Raw ticket counts are only a stopgap until the experience view
       * loads — quotes are cover/date-gated on the same view data, so they are
       * never fetched from the fallback.
       */
      return experienceParticipantOccupants ?? experienceFallbackOccupants
    }

    /**
     * Every source is normalised to the adult/child/infant vocabulary BEFORE the
     * max-per-type merge: a teenager occupies the teenagers field on the hotel
     * side but books as an adult on the flight side, so merging raw occupancies
     * would count the same person once per classification.
     */
    const normalisedAccommodationGroups = accommodationOccupants?.map(normaliseOccupancyForInsurance)
    const normalisedFlightOccupancies = flightOccupancies?.map(normaliseOccupancyForInsurance)

    const baseOccupants = (() => {
      /**
       * If both flight and accommodation occupants exist, merge them using max-per-type algorithm.
       * This ensures consistent traveller counts between quote fetching and order submission.
       *
       * Each accommodation group is spread as its own source: passed together as one
       * source they would collapse to the single group with the highest total, dropping
       * the other offers' travellers from the quote while the hotel/bedbank traveller
       * forms (and the order) still union max-per-type across items.
       *
       * Example:
       * - Hotel room: 2 adults, 1 child (total: 3)
       * - Flight: 3 adults, 0 children (total: 3)
       * - Result: 3 adults, 1 child = 4 travellers
       */
      if (normalisedFlightOccupancies && normalisedAccommodationGroups) {
        return mergeOccupanciesByMaxPerType(normalisedFlightOccupancies, ...occupancyGroupsAsMergeSources(normalisedAccommodationGroups))
      }

      /**
       * We sell cfmr for standalone flights
       * In this case, we need to get the occupants from the flight with highest occupancy
       */
      if (isStandaloneFlight && normalisedFlightOccupancies) {
        return max(normalisedFlightOccupancies, getTotalOccupancyCount)
      }

      if (normalisedFlightOccupancies) {
        return mergeOccupanciesByMaxPerType(normalisedFlightOccupancies)
      }

      /**
       * Multi-offer carts: union the accommodation groups per age group rather than
       * keeping only the group with the highest total. This mirrors the traveller-form
       * union for hotel and bedbank items (svc-traveller shares forms across items, so
       * the form set is the per-type max). Rooms of the same offer are already summed
       * per group upstream; the quote may exceed the form union within a group (the
       * insured list is padded up to the quote), but must never fall below it per age
       * group or the provider rejects the booking after payment. Cruise forms are
       * generated per cabin passenger and sum across cabins, so a multi-cabin cruise
       * cart can still exceed this union — closing that gap needs cruise groups to be
       * summed rather than unioned here.
       */
      if (normalisedAccommodationGroups) {
        return mergeOccupanciesByMaxPerType(...occupancyGroupsAsMergeSources(normalisedAccommodationGroups))
      }
    })()

    /**
     * Mixed carts (accommodation/flights + experiences): the traveller form schema also
     * generates participant forms for experience tickets, and svc-traveller unions forms
     * max-per-type across items. Merge the experience participants into the quote
     * occupancy the same way so the order never submits more travellers than the quote
     * was fetched for.
     */
    if (baseOccupants && experienceParticipantOccupants) {
      return mergeOccupanciesByMaxPerType([baseOccupants], [experienceParticipantOccupants])
    }

    return baseOccupants
  },
)

export const getOccupantsFromExistingOrder = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.experience.bookingDetails,
  (order, flightDetails, experiencesBookingDetails): App.Occupants | undefined => {
    if (!order) return

    // Each vertical is judged by the items that still shape the insured trip: a collection
    // holding only cancelled history neither decides which vertical the occupancy comes
    // from (e.g. a cancelled hotel next to the completed standalone tour being insured)
    // nor double-counts a cancelled item alongside its replacement after a date change.
    const activeItems = order.items.filter(item => item.status !== 'cancelled')
    if (activeItems.length) {
      const reservations = nonNullable(activeItems.map(item => item.reservation))
      return getOccupanciesFromReservations(...reservations)
    }

    const activeBedbankItems = order.bedbankItems.filter(item => item.status !== 'cancelled')
    if (activeBedbankItems.length) {
      return countOccupants(activeBedbankItems.flatMap(item => item.rooms.map(room => room.occupancy)))
    }

    const completedTourItems = order.tourItems.filter(item => item.status === 'completed')
    if (completedTourItems.length) {
      return countTravellersAsOccupants(completedTourItems.flatMap(items => items.tour.travellers))
    }

    const completedCruiseItems = order.cruiseItems.filter(item => item.status === 'completed')
    if (completedCruiseItems.length) {
      return countTravellersAsOccupants(completedCruiseItems.flatMap(items => items.passengers))
    }

    const activeFlightItems = order.flightItems.filter(item => item.status !== 'cancelled')
    if (activeFlightItems.length) {
      if (!Object.keys(flightDetails).length) return

      const flightDetailsByOrderItems = getFlightDetailsByOrderItems(order, flightDetails)
      if (!flightDetailsByOrderItems) return

      const travellers = activeFlightItems.reduce<Array<App.FlightTraveller>>((acc, item) => {
        const currentFlightDetails: App.FlightOrderItemDetails | undefined = flightDetailsByOrderItems[item.id]
        const itemTravellers = currentFlightDetails?.travellers || []
        return [...acc, ...itemTravellers]
      }, [])

      return countTravellersAsOccupants(travellers)
    }

    if (order.experienceItems.some(item => item.status !== 'cancelled')) {
      if (!experiencesBookingDetails) return
      const totalTickets = Object.values(experiencesBookingDetails).filter(booking => booking.orderId === order.id).length

      return {
        adults: totalTickets,
        children: 0,
        infants: 0,
        teenagers: 0,
        childrenAge: [],
      }
    }
  },
)

const EmptyOccupants: App.Occupants = { adults: 0 }

function formatInsuranceTravellers(travellers: App.Occupants | undefined): App.Occupants {
  const normalised = normaliseOccupancyForInsurance(travellers ?? EmptyOccupants)
  // No teenagers field in the output: they are folded into the other counts,
  // and the quote key serialises every present field.
  return {
    adults: normalised.adults,
    children: normalised.children,
    infants: normalised.infants,
    childrenAge: normalised.childrenAge,
    childrenBirthDate: normalised.childrenBirthDate,
  }
}

export const getCheckoutInsuranceTravellers = createSelector(
  (state: App.State) => getOccupantsFromExistingOrder(state),
  (state: App.State) => getOccupantsFromCartItems(state),
  (orderTravellers, cartTravellers): App.Occupants => formatInsuranceTravellers(cartTravellers ?? orderTravellers),
)

export const getCheckoutInsuranceBookingProtectionTravellers = createSelector(
  (state: App.State) => getBookingProtectionOccupantsFromCartItems(state),
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (bookingProtectionTravellers, fallbackTravellers): App.Occupants => formatInsuranceTravellers(bookingProtectionTravellers ?? fallbackTravellers),
)

/**
 * Returns a key representing the current traveller configuration for insurance.
 *
 * Stale quotes are prevented by two complementary mechanisms:
 * 1. Re-keying (primary): getCheckoutSelectedQuotes rebuilds each item's quote
 *    cache key from the CURRENT params on every read, so an occupancy change
 *    makes the old quote unreachable — it goes absent, never stale.
 * 2. Explicit invalidation (this key): useInsuranceTravellerCountInvalidation
 *    watches this key and clears the quote cache when it changes. This also
 *    wipes fetchQuotesErrors — unsticking errored keys that would otherwise
 *    never refetch — and covers quotes keyed by an item's own custom
 *    travellers, which re-keying cannot see cart occupancy changes for.
 */
export const getCheckoutInsuranceTravellerKey = createSelector(
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (travellers): string => {
    if (!travellers) return '0-0-0'
    return `${travellers.adults ?? 0}-${travellers.children ?? 0}-${travellers.infants ?? 0}`
  },
)
